import { parseNum, siteLabel, specsFor, specsText, todayIso, type ItemSpecs, t } from '@procumi/shared';
import { useEffect, useState } from 'react';
import { CofrajHelper } from '../components/CofrajHelper';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import type { Route } from '../routes';
import { DateInput } from '../components/DateInput';
import { isBuild } from '../lib/terms';

interface Line {
  key: string;
  desc: string;
  qty: string;
  unit: string;
  /** din chestionarul de cofraje: chiria, în zile; null = cumpărare */
  rentDays?: number | null;
  specs?: ItemSpecs | null;
}

/**
 * „Cofraje metalice, 200 mp” nu spune furnizorului ce să oferteze: lipsesc
 * elementul și înălțimea. Atunci, sub rând, îl trimitem la chestionar.
 */
const vagueCofraj = (l: Line) => /cofra/i.test(l.desc) && !specsFor(l.desc, l.specs) && l.rentDays == null;

interface Draft {
  siteId: string;
  title: string;
  category: string;
  neededBy: string;
  note: string;
  lines: Line[];
}

let seq = 0;
const line = (): Line => ({ key: `l${++seq}`, desc: '', qty: '', unit: 'buc' });
const fresh = (): Draft => ({
  siteId: '',
  title: '',
  category: '',
  neededBy: '',
  note: '',
  lines: [line()],
});

/** Unitățile de pe șantier, propuse la scriere; se poate scrie și alta. */
const UNITS = [t('buc'), t('mp'), t('mc'), t('ml'), 'kg', 't', t('sac'), t('palet'), 'set', t('rolă'), 'l'];

/** Ciorna rămâne dacă omul trece pe altă pagină și revine. */
let stored: Draft | null = null;

/**
 * Necesarul de pe șantier: ce materiale trebuie, cât și până când. Gândit
 * pentru telefon, scris din mers: câmpuri mari, fără prețuri, fără furnizori.
 * Achizițiile primesc un mail și îl găsesc pe panou.
 */
export function NewRequisitionPage({
  go,
  toast,
  siteId,
  editId,
}: {
  go: (r: Route) => void;
  toast: Toast;
  siteId?: string;
  /** necesarul de modificat; lipsă = necesar nou */
  editId?: string;
}) {
  const [draft, setDraft] = useState<Draft>(() => {
    if (editId) return fresh();
    const base = stored ?? fresh();
    return siteId ? { ...base, siteId } : base;
  });
  // La editare: necesarul de acum, pus în formular o singură dată.
  const editQ = useAsync(
    () => (editId ? api.requisition(editId) : Promise.resolve(null)),
    [editId],
    `requisition:${editId ?? '-'}`,
  );
  const editing = editQ.data;
  const [loaded, setLoaded] = useState(!editId);
  useEffect(() => {
    if (!editing || loaded) return;
    setDraft({
      siteId: editing.site.id,
      title: editing.title,
      category: editing.category,
      neededBy: editing.neededBy ?? '',
      note: editing.note,
      lines: editing.items.map((i) => ({
        ...line(),
        desc: i.desc,
        qty: String(i.qty).replace('.', ','),
        unit: i.unit,
        rentDays: i.rentDays ?? null,
        specs: i.specs ?? null,
      })),
    });
    setLoaded(true);
  }, [editing, loaded]);
  const [busy, setBusy] = useState(false);
  const [cofraj, setCofraj] = useState(false);
  /** rândul vag din care s-a deschis chestionarul: poziția completă îi ia locul */
  const [replacing, setReplacing] = useState<string | null>(null);
  const sitesQ = useAsync(() => api.requisitionSites(), [], 'requisition-sites');
  const sites = sitesQ.data ?? [];
  const catsQ = useAsync(() => api.requisitionCategories(), [], 'requisition-categories');
  const cats = (catsQ.data?.used.length ? catsQ.data.used : catsQ.data?.suggested) ?? [];
  // la cofraje, chestionarul stă deschis, ca la cererea de ofertă
  const cofrajCat = isBuild() && /cofra/i.test(draft.category);

  useEffect(() => {
    // ciorna păstrată e doar a necesarului nou
    if (!editId) stored = draft;
  }, [draft, editId]);

  // Un singur șantier deschis: e al lui, nu-l mai punem să aleagă.
  useEffect(() => {
    if (draft.siteId || sites.length !== 1) return;
    const only = sites[0]!.id;
    setDraft((d) => (d.siteId ? d : { ...d, siteId: only }));
  }, [sites, draft.siteId]);

  const written = !!draft.title.trim() || draft.lines.some((l) => l.desc.trim() || l.qty.trim());
  useEffect(() => {
    if (!written) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [written]);

  const patch = (p: Partial<Draft>) => setDraft((d) => ({ ...d, ...p }));
  const setLine = (key: string, p: Partial<Line>) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l) => (l.key === key ? { ...l, ...p } : l)),
    }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const filled = draft.lines.filter((l) => l.desc.trim());
    const noQty = filled.find((l) => {
      const q = parseNum(l.qty);
      return q == null || q <= 0;
    });
    if (!draft.siteId) return toast(t('Alege șantierul'));
    if (!draft.title.trim()) return toast(t('Scrie pe scurt ce trebuie'));
    if (!draft.category) return toast(t('Alege categoria'));
    if (!filled.length) return toast(t('Adaugă cel puțin un material'));
    if (noQty) return toast(t('Scrie cantitatea pentru „{0}”', noQty.desc.trim()));
    setBusy(true);
    try {
      const body = {
        siteId: draft.siteId,
        title: draft.title.trim(),
        category: draft.category,
        neededBy: draft.neededBy || null,
        note: draft.note.trim(),
        items: filled.map((l) => ({
          desc: l.desc.trim(),
          qty: parseNum(l.qty)!,
          unit: l.unit.trim() || 'buc',
          rentDays: l.rentDays ?? null,
          specs: l.specs ?? null,
        })),
      };
      if (editId) {
        await api.updateRequisition(editId, body);
        toast(t('Necesarul a fost modificat'));
        go({ view: 'requisition', id: editId });
        return;
      }
      const r = await api.createRequisition(body);
      stored = null;
      toast(t('Necesarul {0} a plecat la achiziții', r.number));
      go({ view: 'requisition', id: r.id });
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (sitesQ.loading || editQ.loading || !loaded) {
    if (editQ.error) return <ErrorBox error={editQ.error} onRetry={editQ.reload} />;
    return <Loading />;
  }
  if (sitesQ.error) return <ErrorBox error={sitesQ.error} onRetry={sitesQ.reload} />;
  if (editing && editing.status !== 'open') {
    return (
      <ErrorBox
        error={t(
          'Necesarul {0} nu se mai poate modifica: a fost preluat, respins sau retras.',
          editing.number,
        )}
      />
    );
  }

  return (
    <>
      <div className="head">
        <div>
          {editing ? (
            <button className="back" onClick={() => go({ view: 'requisition', id: editing.id })}>
              ‹ {editing.number}
            </button>
          ) : null}
          <h1>{editing ? t('Editează {0}', editing.number) : t('Necesar nou')}</h1>
          <p>
            {editing
              ? t('Poți schimba orice cât timp achizițiile nu l-au preluat într-o cerere de ofertă.')
              : t(
                  'Scrie ce materiale trebuie pe șantier și până când. Achizițiile cer oferte și comandă; tu vezi aici unde a ajuns.',
                )}
          </p>
        </div>
      </div>

      <form className="sheet pad req-form" onSubmit={(e) => void submit(e)} noValidate>
        <div className="row">
          <label className="f" htmlFor="req-site">
            {t('Șantier')}
            <select
              id="req-site"
              className="inp"
              value={draft.siteId}
              onChange={(e) => patch({ siteId: e.target.value })}
            >
              <option value="">{t('Alege șantierul…')}</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {siteLabel(s)}
                </option>
              ))}
            </select>
            {!sites.length ? (
              <span className="hint" style={{ margin: 0 }}>
                {t('Nu e niciun șantier deschis. Roagă achizițiile să-l adauge.')}
              </span>
            ) : null}
          </label>
          <label className="f" htmlFor="req-needed">
            {t('Trebuie pe șantier la')}
            <DateInput
              id="req-needed"
              min={todayIso()}
              value={draft.neededBy}
              onChange={(v) => patch({ neededBy: v })}
            />
            <span className="hint" style={{ margin: 0 }}>
              {t('opțional, dar ajută achizițiile să știe ce e urgent')}
            </span>
          </label>
        </div>

        <div className="row req-what" style={{ marginTop: 'var(--space-4)' }}>
          <label className="f" htmlFor="req-title">
            {t('Ce trebuie, pe scurt')}
            <input
              id="req-title"
              className="inp"
              value={draft.title}
              maxLength={200}
              placeholder={t('ex. Armătură pentru placa de peste etajul 3')}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </label>
          <label className="f" htmlFor="req-cat">
            {t('Categorie')}
            <select
              id="req-cat"
              className="inp"
              value={draft.category}
              onChange={(e) => patch({ category: e.target.value })}
            >
              <option value="">{t('Alege categoria…')}</option>
              {cats.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <span className="hint" style={{ margin: 0 }}>
              {t('după ea, achizițiile știu căror furnizori să trimită')}
            </span>
          </label>
        </div>

        <h2 className="req-form-h">{t('Materiale')}</h2>
        <datalist id="req-units">
          {UNITS.map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>
        <ol className="req-lines">
          {draft.lines.map((l, k) => (
            <li key={l.key}>
              <span className="req-n" aria-hidden="true">
                {k + 1}
              </span>
              <input
                className="inp req-desc"
                value={l.desc}
                placeholder={t('Material, cu ce contează (diametru, clasă, marcă)')}
                aria-label={t('Materialul {0}', k + 1)}
                onChange={(e) => setLine(l.key, { desc: e.target.value })}
              />
              <input
                className="inp req-qty"
                inputMode="decimal"
                value={l.qty}
                placeholder={t('Cant.')}
                aria-label={t('Cantitatea pentru materialul {0}', k + 1)}
                onChange={(e) => setLine(l.key, { qty: e.target.value })}
              />
              <input
                className="inp req-unit"
                list="req-units"
                value={l.unit}
                aria-label={t('Unitatea de măsură pentru materialul {0}', k + 1)}
                onChange={(e) => setLine(l.key, { unit: e.target.value })}
              />
              {draft.lines.length > 1 ? (
                <button
                  type="button"
                  className="x req-x"
                  aria-label={t('Scoate materialul {0}', k + 1)}
                  onClick={() => patch({ lines: draft.lines.filter((x) => x.key !== l.key) })}
                >
                  ×
                </button>
              ) : (
                <span className="req-x" />
              )}
              {l.rentDays || specsText(l.specs) ? (
                <span className="req-spec">
                  {[
                    specsText(l.specs),
                    l.rentDays ? `chirie ${l.rentDays} ${l.rentDays === 1 ? t('zi') : t('zile')}` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              ) : vagueCofraj(l) ? (
                <span className="req-warn" role="note">
                  {t(
                    'Cofrajul are nevoie de element, înălțime și durată, altfel furnizorii nu știu ce să oferteze.',
                  )}
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      setReplacing(l.key);
                      setCofraj(true);
                    }}
                  >
                    {t('Folosește „Poziție de cofraj”')}
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ol>
        <div className="req-add">
          <button
            type="button"
            className="btn small"
            onClick={() => patch({ lines: [...draft.lines, line()] })}
          >
            {t('Încă un material')}
          </button>
          {cofrajCat || !isBuild() ? null : (
            <button
              type="button"
              className="btn small"
              aria-expanded={cofraj}
              onClick={() => setCofraj(!cofraj)}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                <path
                  d="M2.5 2.5h11v11h-11zM2.5 6.2h11M2.5 9.8h11M6.2 2.5v11M9.8 2.5v11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.3"
                />
              </svg>
              {t('Poziție de cofraj')}
            </button>
          )}
        </div>
        {cofraj || cofrajCat ? (
          <CofrajHelper
            submitLabel={t('Adaugă în materiale')}
            onAdd={(list) => {
              setReplacing(null);
              setDraft((d) => {
                const added: Line[] = list.map((c) => ({
                  ...line(),
                  desc: c.desc,
                  qty: String(c.qty).replace('.', ','),
                  unit: c.unit,
                  rentDays: c.rentDays,
                  specs: c.specs,
                }));
                // pornit din avertismentul unui rând: poziția completă îl înlocuiește
                const at = d.lines.findIndex((x) => x.key === replacing);
                if (at >= 0) {
                  return {
                    ...d,
                    lines: [...d.lines.slice(0, at), ...added, ...d.lines.slice(at + 1)],
                  };
                }
                // rândul gol de la început se înlocuiește, nu rămâne în urmă
                const kept = d.lines.filter((x) => x.desc.trim() || x.qty.trim());
                return { ...d, lines: [...kept, ...added] };
              });
            }}
          />
        ) : null}

        <label className="f" htmlFor="req-note" style={{ marginTop: 'var(--space-6)' }}>
          {t('Pentru achiziții (opțional)')}
          <textarea
            id="req-note"
            className="inp"
            rows={3}
            maxLength={1000}
            value={draft.note}
            placeholder={t('ex. Descărcare doar dimineața, macaraua e liberă până la 12')}
            onChange={(e) => patch({ note: e.target.value })}
          />
        </label>

        <div className="req-submit">
          {editId ? (
            <>
              <button className="btn primary" type="submit" disabled={busy}>
                {busy ? t('Se salvează…') : t('Salvează modificările')}
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => go({ view: 'requisition', id: editId })}
              >
                {t('Renunță')}
              </button>
              <span className="hint" style={{ margin: 0 }}>
                {t('Achizițiile văd modificarea în clopoțel.')}
              </span>
            </>
          ) : (
            <>
              <button className="btn primary" type="submit" disabled={busy}>
                {busy ? t('Se trimite…') : t('Trimite necesarul')}
              </button>
              <span className="hint" style={{ margin: 0 }}>
                {t('Achizițiile primesc un mail. Tu primești unul când îl preiau.')}
              </span>
            </>
          )}
        </div>
      </form>
    </>
  );
}
